<template>
  <div class="global-search-dialog">
    <el-input :placeholder="$i18n.t('global-search')" v-model="globalSearch" @change="load">
      <el-button slot="append" icon="el-icon-search" @click="load"></el-button>
    </el-input>

    <div class="p-3">{{ $i18n.t('global-search-description') }}</div>

    <el-tabs type="card">
      <!-- Entities Tab -->
      <el-tab-pane>
        <!-- Tab Label -->
        <span slot="label">
          {{ $i18n.t('global-search-entities-tab') }}
          <span v-if="foundEntities" :class="badgeClass">
            {{ entities.resultCount }}
          </span>
        </span>
        <!-- Tab Content -->
        <div class="tab-content">
          <h4 v-if="actualSearch" class="text-right pull-right">
            {{ $i18n.t('number-of-entites') }}&nbsp;
            <span :class="badgeClass"> {{ entities.entities }} / {{ entities.entitiesCount }} </span>
          </h4>

          <div v-if="entities.resultCount">
            <div v-for="item in entities.searchResults" :key="item.title">
              <h3>{{ $i18n.t(item.form.formName) }}</h3>
              <div v-for="(element, key) in item.result" :key="`${item.title}-result-${key}`" class="row result-row">
                <table class="table">
                  <col width="30%" />
                  <tbody>
                    <tr>
                      <th>{{ $i18n.t(item.form.gui.primaryInList) }}</th>
                      <td v-html="highlight(element[item.form.gui.primaryInList], actualSearch).html"></td>
                    </tr>

                    <tr
                      v-for="(field, key) in element"
                      :key="key"
                      v-if="
                        key != item.form.gui.primaryInList &&
                        item.form.gui.availableInList.includes(key) &&
                        highlight(field, actualSearch).success
                      "
                    >
                      <th>{{ $i18n.t(key) }}</th>
                      <td v-html="highlight(field, actualSearch).html"></td>
                    </tr>
                  </tbody>
                </table>

                <el-collapse v-model="activeCollapses" :name="`${item.title}-result-${key}`">
                  <el-collapse-item :title="$i18n.t('global-search-more-details')">
                    <table class="table">
                      <col width="30%" />
                      <tbody>
                        <tr
                          v-for="(field, key) in element"
                          :key="key"
                          v-if="
                            key != item.form.gui.primaryInList &&
                            item.form.gui.availableInList.includes(key) &&
                            !highlight(field, actualSearch).success
                          "
                        >
                          <th>{{ $i18n.t(key) }}</th>
                          <td v-html="highlight(field, actualSearch).html"></td>
                        </tr>
                      </tbody>
                    </table>
                  </el-collapse-item>
                </el-collapse>
              </div>
            </div>
          </div>
          <div v-if="!entities.resultCount && !loading">
            <h4 class="text-center">
              {{ $i18n.t('global-search-not-found') }}
            </h4>
          </div>
        </div>

        <div class="text-center" v-if="loading">
          <loader></loader>
        </div>
      </el-tab-pane>

      <!-- Menus Tab -->
      <el-tab-pane>
        <span slot="label">
          {{ $i18n.t('global-search-menus-tab') }}
          <span v-if="foundMenus" :class="badgeClass">
            {{ menus.resultCount }}
          </span>
        </span>

        <div class="tab-content" v-if="menus.resultCount">
          <router-link v-for="item in menus.searchResults" :key="item.id" class="row result-row" :to="item.internalUrl">
            <h4 v-html="highlight(item.label, actualSearch).html"></h4>
          </router-link>
        </div>

        <div class="tab-content" v-if="!menus.resultCount && !loading">
          <h4 class="text-center">{{ $i18n.t('global-search-not-found') }}</h4>
        </div>

        <div class="text-center" v-if="loading">
          <loader></loader>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import Loader from '../SimpleLoader'
import listRest from '../../utils/mixin-list-rest'
import { highlight } from '../../utils'

/**
 * @class GlobalSearch
 *
 * @description This component renders the Global search interface
 */
export default {
  name: 'global-search',
  mixins: [listRest],
  components: {
    Loader,
  },
  props: {
    isOpen: {
      type: Boolean,
    },
  },
  data() {
    return {
      actualSearch: '',
      activeCollapses: [],
      loading: false,
      entities: {
        searchResults: [],
        resultCount: 0,
        entitiesCount: 0,
        entities: 0,
      },
      menus: {
        searchResults: [],
        resultCount: 0,
        menuItems: 0,
      },
    }
  },
  computed: {
    globalSearch: {
      get() {
        return this.$store.getters.globalSearch
      },
      set(value) {
        this.$store.dispatch('setGlobalSearch', value)
      },
    },
    badgeClass() {
      return `el-badge__content el-badge__content--${this.loading ? 'warning' : 'success'}`
    },
    foundEntities() {
      return this.entities.resultCount > 0
    },
    foundMenus() {
      return this.menus.resultCount > 0
    },
  },
  methods: {
    highlight,
    async loadEntites() {
      this.entities.searchResults = []
      this.entities.resultCount = 0
      this.entities.entitiesCount = 0
      this.entities.entities = 0

      const entities = await this.$client.apis['Search']['Search_getProperties']()

      this.entities.entitiesCount = entities.body.length
      for (let index = 0; index < entities.body.length; index++) {
        const element = entities.body[index]
        this.entities.entities++

        let parameters = {
          formId: element.formId,
          panelId: this.panelId,
          u: this.globalSearch,
          length: 10,
        }

        let { body } = await this.getList(parameters)

        if (body && body.data && body.data.length) {
          this.entities.searchResults.push({
            form: element,
            result: body.data,
            title: element.formId || this.entities.searchResults.length,
          })

          this.entities.resultCount += body.data.length
        }
      }
    },
    async loadMenu() {
      this.menus.searchResults = []
      this.menus.resultCount = 0
      this.menus.menuItems = 0

      const parameters = {
        filter: JSON.stringify({
          where: {
            subSystemCode: window.CORE.subSystem.code,
          },
        }),
      }
      const menus = await this.$client.execute({
        operationId: 'Menu_findOne',
        parameters,
      })

      const exp = new RegExp(this.globalSearch, 'ig')
      const filteredItems = menus.obj.items.filter((item) => item.label.match(exp))
      this.menus.searchResults = filteredItems
      this.menus.resultCount = filteredItems.length
    },
    async load() {
      if (!this.globalSearch) return

      this.loading = true
      this.actualSearch = this.globalSearch

      const promiseEntities = this.loadEntites(this.globalSearch)
      const promiseMenu = this.loadMenu(this.globalSearch)
      Promise.all([promiseEntities, promiseMenu]).then((values) => {
        this.loading = false
      })
    },
  },
  created() {
    this.load()
  },
  watch: {
    isOpen(newVal) {
      if (newVal && this.actualSearch !== this.globalSearch) {
        this.load()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tab-content {
  padding: 20px;
  border-top: 1px solid $border-color;
  border-radius: 8px;
  border-top-left-radius: 0;
  background-color: $white;
}
.result-col {
  word-break: break-all;
}
a.result-row:hover {
  color: #111;
}
.result-row {
  display: block;
  background-color: $light1;
  border: 1px solid $border-color;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 10px;
}
.result-row:hover {
  border: 1px solid $color-primary-hover;
}
</style>
<style lang="scss">
.highlight {
  background-color: $green;
  border-radius: 2px;
}
</style>
